import {
  combineReducers,
  type Reducer,
  type ReducersMapObject,
} from '@reduxjs/toolkit';

import type { RootState } from './rootReducer';

export type ReducerManager = {
  /** The currently combined reducer, including any injected slices. */
  getReducer: () => Reducer<RootState>;
  /** Registers a slice reducer at runtime. No-op if it is already present. */
  add: (key: string, reducer: Reducer) => void;
  /** Removes a previously injected slice reducer. Static reducers cannot be removed. */
  remove: (key: string) => void;
};

/**
 * Builds a reducer that can accept additional slices after the store is created.
 *
 * `staticReducers` are always present (core app + pre-auth needs). Feature slices
 * are injected via `add()` when the route/feature that owns them is lazily loaded,
 * so their (potentially heavy) module graphs stay out of the initial bundle.
 *
 * The returned reducer is typed as `Reducer<RootState>` so existing selectors and
 * thunks continue to see the full state shape even before a slice is injected.
 * Selectors that read lazily-injected slices must tolerate an `undefined` slice
 * for the window before injection (the geofence selectors already do this).
 *
 * @param staticReducers - Reducers that are always mounted on the store.
 * @returns A manager exposing the combined reducer plus add/remove helpers.
 */
export const createReducerManager = (
  staticReducers: ReducersMapObject
): ReducerManager => {
  const reducers: ReducersMapObject = { ...staticReducers };
  const injectedKeys = new Set<string>();
  const staticKeys = new Set<string>(Object.keys(staticReducers));
  let combined = combineReducers(reducers);

  return {
    getReducer: () => combined as Reducer<RootState>,

    add: (key, reducer) => {
      if (staticKeys.has(key) || injectedKeys.has(key)) return;
      reducers[key] = reducer;
      injectedKeys.add(key);
      combined = combineReducers(reducers);
    },

    remove: (key) => {
      if (staticKeys.has(key) || !injectedKeys.has(key)) return;
      delete reducers[key];
      injectedKeys.delete(key);
      combined = combineReducers(reducers);
    },
  };
};
